import React from 'react'
import classNames from 'classnames'
import { IconArrowUp, IconArrowDown } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { truncateAgentName } from '@/utils/helpers'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { InfoTooltip } from '@/components/tooltips/InfoTooltip'

import { Pill } from '../pills/Pill'

import './AnalyticsCard.scss'

const determineArrow = (variant, arrowUp) => {
  if (variant === 'neutral') return null

  if (arrowUp) {
    return <IconArrowUp data-testid="up-arrow" />
  }
  return <IconArrowDown data-testid="down-arrow" />
}

/**
 * @import import { AnalyticsCard } from '@/components/cards/AnalyticsCard'
 * @param {object} props
 * @param {node} props.data Headline value, already formatted for display
 * @param {'positive'|'negative'|'neutral'} [props.variant] Colours the trend pill; neutral renders it emphasized with no arrow or % sign
 * @param {node} props.description Label above the value
 * @param {node} [props.dateDescription] Caption under the value, e.g. the comparison baseline
 * @param {boolean} [props.hideDateDescription] Omits the caption row
 * @param {number|string} [props.percent] Trend pill value; 0 reads "No Change", null hides the pill
 * @param {boolean} [props.arrowUp] Points the pill arrow up (true) or down (false)
 * @param {boolean} [props.loading] Replaces the value with a Loader
 * @param {boolean} [props.comparisonsLoading] Replaces value, pill and caption with one Loader while the baseline loads
 * @param {string} [props.testId] Prefix for data-testid on the card and its parts (-description, -value, -percent-change, -date-description)
 * @param {string} [props.displayDataClass] Class on the value element; analytics-card-empty-data de-emphasises an empty value
 * @param {boolean} [props.enableAnalyticsCardPopup] Truncates a long value and shows the full text in a tooltip
 * @param {node} [props.icon] Icon rendered before the label
 * @param {'magical'|'brand'|'success'|'critical'} [props.accent] Tints the icon and value for a subject with its own colour
 * @param {node} [props.info] InfoTooltip content shown next to the label
 * @param {Function} [props.onClick] Makes the whole card a button; don't combine with info, which is its own button
 */
export const AnalyticsCard = ({
  data,
  variant = 'neutral',
  description,
  dateDescription,
  hideDateDescription = false,
  percent,
  arrowUp,
  loading,
  comparisonsLoading,
  testId = 'analytics-card',
  displayDataClass = 'analytics-card-data',
  enableAnalyticsCardPopup = false,
  icon,
  accent,
  info,
  onClick,
}) => {
  const displayCardData = (data) => {
    if (enableAnalyticsCardPopup) {
      return (
        <InteractiveTooltip interactive content={data}>
          <div className="analytics-data-truncate-name">{truncateAgentName(data)}</div>
        </InteractiveTooltip>
      )
    }
    return data
  }

  const isPositive = variant === 'positive'
  const isNegative = variant === 'negative'

  const pillText =
    parseInt(percent, 10) !== 0
      ? `${isPositive ? '+' : ''}${isNegative ? '-' : ''}${percent}${variant !== 'neutral' ? '%' : ''}`
      : 'No Change'

  const Container = onClick ? 'button' : 'div'

  return (
    <Container
      data-testid={testId}
      className={classNames('analytics-card', accent && `accent-${accent}`, percent && variant, {
        clickable: onClick,
      })}
      {...(onClick && { type: 'button', onClick })}
    >
      <div className="analytics-card-content">
        <div data-testid={`${testId}-description`} className="sub-characters">
          {icon && <span className="analytics-card-icon">{icon}</span>}
          {description}
          {info && <InfoTooltip content={info} />}
        </div>
        {comparisonsLoading ? (
          <Loader />
        ) : (
          <div data-testid={`${testId}-percent-change-container`} className="percent-changed-block">
            <div className="analytics-data-percent-block">
              <div className={displayDataClass} data-testid={`${testId}-value`}>
                {loading ? <Loader /> : displayCardData(data)}
              </div>
              {(percent === 0 || percent) && (
                <Pill
                  emphasized={variant === 'neutral'}
                  success={isPositive}
                  critical={isNegative}
                  alignIcon="right"
                  icon={determineArrow(variant, arrowUp)}
                  data-testid={`${testId}-percent-change`}
                >
                  <span data-testid={`${testId}-percent-change-text`}>{pillText}</span>
                </Pill>
              )}
            </div>
            {!hideDateDescription && (
              <div
                data-testid={`${testId}-date-description`}
                className="sub-characters-description"
              >
                {dateDescription}
              </div>
            )}
          </div>
        )}
      </div>
    </Container>
  )
}
